// @ts-nocheck

import React, { useState, useEffect } from 'react';
import { Route, Routes, Navigate, Outlet, useSearchParams } from 'react-router-dom';
import './index.css';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faMagicWandSparkles } from '@fortawesome/free-solid-svg-icons';
import Login from './pages/Login';
import RateCard from './pages/RateCard';
import InviteAccept from './pages/InviteAccept';
import TeamOverview from './pages/super/TeamOverview';
import CommandDeck from './pages/super/CommandDeck';
// import FieldStudio from './pages/super/FieldStudio';
// Platform MCP catalog is off for now: every person connects their own MCP servers.
// import McpCatalog from './pages/super/McpCatalog';
import { IDENTITY_CHANGED_EVENT } from './services/identity';
import Signup from './pages/Signup';
import DirectEntry from './pages/DirectEntry';
import LegacyCompanySetupRedirect from './pages/LegacyCompanySetupRedirect';
import WorkspaceReady from './pages/WorkspaceReady';
import OAuthCallback from './pages/OAuthCallback';
import OrbitEnter from './pages/OrbitEnter';
import Layout from './components/Layout';
import KnowledgeBase from './pages/KnowledgeBase';
import DocumentUpload from './pages/DocumentUpload';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './styles/toast-aa.css';
import Prompts from './pages/Prompts';
import Integrations from './pages/Integrations';
import ChatInterface from './components/ChatInterface';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ProtectedRoute from './components/ProtectedRoute';
import Profile from './pages/Profile';
import CalendarSettings from './pages/CalendarSettings';
import CrawlDetails from './pages/CrawlDetails';
import CitationAnalyticsPage from './pages/CitationAnalyticsPage';
import CitationsPage from './pages/CitationsPage';
import UnifiedAnalytics from './pages/UnifiedAnalytics';
import ClientDashboard from './pages/client/ClientDashboard';
import ChatHistory from './pages/ChatHistory';
import RoleBasedRoute from './components/RoleBasedRoute';
import PermissionRoute from './components/PermissionRoute';
import { ImpersonationProvider } from './contexts/ImpersonationContext';
import CrawlProgressIndicator from './components/CrawlProgressIndicator';
import CrawlProgressPage from './components/CrawlProgress/CrawlProgressPage';

// Public pages
import StandalonePreview from './pages/public/StandalonePreview';
import SecureFormPage from './pages/public/SecureFormPage';
import SdkDocsPage from './pages/public/SdkDocsPage';

// Orbit Layer 1 — new structured onboarding intake
import { OrbitIntake } from './orbit/OrbitIntake';
import ConnectClaude from './pages/ConnectClaude';
import { OrbitDelegated } from './orbit/OrbitDelegated';
// Orbit Layer 2 — scoped problem cards + synthesis
import { OrbitOptions } from './orbit/stage1/OrbitOptions';
// Orbit Stage 2 outcomes (bypass-eligible speed-rails)
import OrbitDeploy from './orbit/outcomes/OrbitDeploy';
import OrbitTemplate from './orbit/outcomes/OrbitTemplate';
import OrbitHandoff from './orbit/outcomes/OrbitHandoff';
import MarketingLayout from './layouts/MarketingLayout';

// Consultant pages
import StaffShell from './layouts/StaffShell';
import ConsultantDashboard from './pages/consultant/ConsultantDashboard';
import ConsultantsList from './pages/consultant/ConsultantsList';
import ConsultantForm from './pages/consultant/ConsultantForm';
import ConsultantDetails from './pages/consultant/ConsultantDetails';
import SEOCenter from './pages/consultant/SEOCenter';
import SEODashboardModern from './pages/consultant/SEODashboardModern';
import ChatbotTraining from './pages/ChatbotTraining';
import SupportFlowEditor from './pages/consultant/SupportFlowEditor';
import AgentsManagement from './pages/consultant/AgentsManagement';
import ConsultantSettings from './pages/consultant/ConsultantSettings';
import NewClient from './pages/consultant/NewClient';
import ClientDetail from './pages/consultant/ClientDetail';
// import ConsultantClientAdoption from './pages/consultant/ConsultantClientAdoption';
// import PartnerDashboardPage from './pages/partner/DashboardPage';
import PartnerClientsPage from './pages/partner/ClientsPage';
import PartnerNotFound from './pages/partner/PartnerNotFound';
import NotFound from './pages/NotFound';
import PartnerClientOverviewPage from './pages/partner/ClientOverviewPage';
// import PartnerSchedulePage from './pages/partner/SchedulePage';
import PartnerProjectsPage from './pages/partner/ProjectsPage';
import PartnerClientContextPage from './pages/partner/ClientContextPage';
// import PartnerEngagementBoard from './pages/partner/EngagementBoard';
import PartnerDeliveryProject from './pages/partner/DeliveryProjectPage';
import PartnerAddClient from './pages/partner/AddClient';
import PartnerSettings from './pages/partner/PartnerSettings';
import PersonIntake from './pages/PersonIntake';
import PartnerClientSettings from './pages/partner/ClientSettingsModal';
// import ConsultantProgramsPage from './pages/aai/consultant/ConsultantProgramsPage';
// import ConsultantDeliveryPage from './pages/aai/consultant/ConsultantDeliveryPage';
// import ConsultantAssetsPage from './pages/aai/consultant/ConsultantAssetsPage';
// GuestOnboarding removed — superseded by /orbit/intake. New signups land
// in Orbit directly (Signup.tsx). Returning users hit resolvePostLoginRoute
// which sends them to route_plan.next_best_action.route or /orbit/intake.

// Client pages
import ClientBillingDashboard from './pages/client/BillingDashboard';
import { ProjectsScreen } from './pages/client/ClientAgentsPage';
import ProblemDetail from './pages/client/ProblemDetail';
// H1: grouped adoption surfaces. Each hub mounts pages that also keep their own
// route below, so nothing became unreachable when the sidebar shrank.
import {
  AdoptionHomeHub, UseCasesHub, PeopleHub, MyToolsHub, HelpHub, MyGrowthHub,
  DepartmentHub, ProgramHub,
} from './pages/aai/hubs';
// import AaiWorkspace from './pages/aai/AaiWorkspace';
import MyTrainingPage from './pages/aai/MyTrainingPage';
import ToolLibraryPage from './pages/aai/ToolLibraryPage';
import SkillBuilderPage from './pages/aai/SkillBuilderPage';
import OnboardingPage from './pages/aai/OnboardingPage';
import DeckPage from './pages/orbit-deck/DeckPage';
import SetupPage from './pages/setup/SetupPage';
import FrameworkPage from './pages/partner/FrameworkPage';
import PartnerJoin from './pages/partner/PartnerJoin';
import PartnerWelcome from './pages/partner/PartnerWelcome';
import RequestsPage from './pages/aai/RequestsPage';
import NotificationsPage from './pages/aai/NotificationsPage';
import DeptUseCasesPage from './pages/aai/DeptUseCasesPage';
import MyProductivityPage from './pages/aai/insights/MyProductivityPage';
// The employee portal (Platform UI Guide IA): Home, Quick Wins, Projects,
// AI Adoption, Results, Company Context.
import EmployeeProjectsPage from './pages/employee/ProjectsPage';
import EmployeeAdoptionPage from './pages/employee/AdoptionPage';
import CompanyContextPage from './pages/employee/CompanyContextPage';
import OwnerProjectsPage from './pages/owner/ProjectsPage';
// import OwnerPortfolioPage from './pages/owner/PortfolioPage';
import OwnerCompanyContextPage from './pages/owner/CompanyContextPage';
// Documents hidden for now.
// import { DocumentsPage } from './pages/partner/DocumentsTab';
import PeoplePage from './pages/PeoplePage';
import UsagePage from './pages/UsagePage';
import ProviderClientsPage from './pages/provider/ProviderClientsPage';
import StartScopingPage from './pages/scope/StartScopingPage';
import ConnectorsPage from './pages/company/ConnectorsPage';
import BillingPage from './pages/company/BillingPage';
import ProviderClientPage from './pages/provider/ProviderClientPage';
import ProviderClientProjectsPage from './pages/provider/ProviderClientProjectsPage';
import { OwnerProjectBudgetPage, ProviderProjectBudgetPage } from './pages/billing/ProjectBudgetPage';
import OwnerAdoptionPage from './pages/owner/AdoptionPage';
import OwnerAiCapabilitiesPage from './pages/owner/AiCapabilitiesPage';
import OwnerInvoicesPage from './pages/owner/InvoicesPage';
import OwnerPersonUsagePage from './pages/owner/PersonUsagePage';
import ProjectRecordPage from './pages/owner/ProjectRecordDrawer';
import { OpportunityPage } from './pages/owner/OpportunityDrawer';
import { QuickWinPage } from './pages/owner/CapabilityDrawer';
import DepartmentProjectsPage from './pages/department/ProjectsPage';
import DepartmentAdoptionPage from './pages/department/AdoptionPage';
import DepartmentCompanyContextPage from './pages/department/CompanyContextPage';
// import ChampionPortfolioPage from './pages/champion/PortfolioPage';
import ChampionProjectsPage from './pages/champion/ProjectsPage';
import ChampionAdoptionPage from './pages/champion/AdoptionPage';
import ChampionCompanyContextPage from './pages/champion/CompanyContextPage';
import ChampionRegistryPage from './pages/champion/RegistryPage';
import TrainerAdoptionPage from './pages/trainer/AdoptionPage';
import ChampionVendorsPage from './pages/champion/VendorsPage';
import OrbitRequest from './pages/employee/OrbitRequest';
import AaiMyToolsPage from './pages/aai/insights/MyToolsPage';
import ProgramReportsPage from './pages/aai/insights/ProgramReportsPage';
import AaiProgramPage from './pages/aai/insights/AaiProgramPage';
import DeliveryPage from './pages/aai/cockpit/DeliveryPage';
import CohortsPage from './pages/aai/cockpit/CohortsPage';
import MySessionsPage from './pages/aai/cockpit/MySessionsPage';
import DepartmentsAdminPage from './pages/aai/cockpit/DepartmentsAdminPage';
import SkillsMarketplacePage from './pages/aai/SkillsMarketplacePage';
import PrdReviewsPage from './pages/aai/PrdReviewsPage';
import MyWorkPage from './pages/expert/MyWorkPage';
import MyBusinessHome from './pages/MyBusinessHome';
import ClientMyToolsPage from './pages/MyToolsPage';
import { useParticipant } from './hooks/useParticipant';
import ExperienceReportPage from './pages/ExperienceReportPage';
import KnowledgeBrowser from './pages/KnowledgeBrowser';
import MarketplacePage from './pages/MarketplacePage';
import ChatbotDetailPage from './pages/ChatbotDetailPage';
import ChatbotPreparing from './pages/ChatbotPreparing';
import MarketplaceSolutionRedirect from './pages/MarketplaceSolutionRedirect';
import CapabilityStudioPage from './pages/CapabilityStudioPage';

// Super user pages
import PlansAndPayments from './pages/super/PlansAndPayments';
import RolesAndIntake from './pages/super/RolesAndIntake';

import { rememberConnectReturn } from './components/settings/PersonalConnect';

// Only add icons needed for non-chat functionality
library.add(faMagicWandSparkles);

// Import debug utilities in development
if (process.env.NODE_ENV === 'development') {
    import('./utils/debug');
}

// /my-tools is two different pages: the AAI-only shells (participant,
// department head) get the adoption view (tool dives, personal skills); every
// full-workspace role (owner, champion, trainer, expert, plain client) keeps
// staging's Tool Scout page. The AAI view has its own home at /aai/my-tools
// for the full-workspace roles (their AI Adoption nav group links it).
// The role portals (Sept 2026). Four surfaces already existed for
// participants and get an Owner-shaped, a Department-Head-shaped and an
// AI-Champion-shaped home; every
// other role keeps the page it had. The role picks the SHELL only: what each
// page may show or do is decided by the permissions it holds, on the server and
// again in the page, never by the role name.
/* The owner's and champion's evaluator lives at /vendors; keep the ?evaluate= and ?open= hand-offs. */
const VendorsRedirect: React.FC = () => {
  const location = useLocation();
  return <Navigate to={`/vendors${location.search}`} replace />;
};

const PortalSwitch: React.FC<{ owner: React.ReactNode; department?: React.ReactNode; champion?: React.ReactNode; trainer?: React.ReactNode; other: React.ReactNode }> = ({ owner, department, champion, trainer, other }) => {
  const { role, ready } = useParticipant();
  if (!ready) return null;
  if (role === 'OWNER') return <>{owner}</>;
  if (role === 'DEPARTMENT_HEAD' && department) return <>{department}</>;
  if (role === 'AI_CHAMPION' && champion) return <>{champion}</>;
  if (role === 'TRAINER' && trainer) return <>{trainer}</>;
  return <>{other}</>;
};

// Where a client lands after login. The owner's main screen is Client, the
// Orbit Room; everyone else keeps Home.
const ClientLanding: React.FC = () => {
  const { role, ready } = useParticipant();
  if (!ready) return null;
  return <Navigate to="/dashboard" replace />;
};

// Employees and department heads read their readiness in a drawer over Home,
// so the old address lands there with it open. Every other role keeps the hub.
const MyReadinessRoute: React.FC = () => {
  const { role, ready } = useParticipant();
  if (!ready) return null;
  if (role === 'AI_PARTICIPANT' || role === 'DEPARTMENT_HEAD') return <Navigate to="/dashboard?open=readiness" replace />;
  return <MyGrowthHub />;
};

/* The old /settings page's tabs, mapped onto the modal's sections. Team and
   Departments are one section there ("People & departments"), and Company
   Context is a screen of its own, so those two do not map to a tab at all. */
const SETTINGS_TAB_SECTION: Record<string, string> = {
  general: 'general',
  calendar: 'calendar',
  routines: 'routines',
  tools: 'connectors',
  team: 'people',
  departments: 'people',
  roles: 'roles',
  connectors: 'connectors',
  billing: 'billing',
  payment: 'payment',
  members: 'people',
  subscription: 'billing',
};

/* /settings, after the page it used to render was folded into the modal. */
const OpenSettings: React.FC = () => {
  const [params] = useSearchParams();
  const section = SETTINGS_TAB_SECTION[params.get('tab') || ''] || 'general';
  const connecting = params.get('connecting');
  if (connecting) rememberConnectReturn(connecting, params.get('scope') || 'personal');
  return <Navigate to="/dashboard" replace state={{ openSettings: section }} />;
};

/* /team, after the page it used to render was folded into Settings. Opens the
   People & departments section and leaves the reader on Home, so an old link
   lands on the surface it was always pointing at rather than on a 404. */
const OpenPeopleSettings: React.FC = () => {
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('settings:open', { detail: { section: 'people' } }));
  }, []);
  return <Navigate to="/dashboard" replace />;
};

/* One tools screen for everybody.
 *
 * It used to fork: an employee and a department head landed on the adoption
 * page, with its dive tracker, its skills zones and its delegation matrix,
 * while everyone else got the connections screen. So "my tools" meant two
 * different things depending on who asked, and only one of them was about
 * tools.
 *
 * What differs per role is the LIST, not the screen, and the server already
 * decides it: the Owner sees every tool a connection can be held to, a
 * department head sees what the Owner gave their department, and an employee
 * sees the same for theirs. The adoption page still exists at /aai/my-tools
 * for the dive work, which is its own thing. */
const MyToolsRoute: React.FC = () => {
  const { ready } = useParticipant();
  if (!ready) return null;
  return <MyToolsHub first={<ClientMyToolsPage />} />;
};

function App() {
    const [showChat, setShowChat] = useState(false);
    const [clientId, setClientId] = useState<string | null>(localStorage.getItem('client_id'));
    const [userRole, setUserRole] = useState<string | null>(localStorage.getItem('user_role'));

    // Monitor localStorage changes
    useEffect(() => {
        const handleStorageChange = () => {
            const storedClientId = localStorage.getItem('client_id');
            const storedRole = localStorage.getItem('user_role');
            
            console.log('Storage changed:', { storedClientId, storedRole });
            
            if (storedClientId !== clientId) {
                setClientId(storedClientId);
            }
            if (storedRole !== userRole) {
                setUserRole(storedRole);
            }
        };

        // Cross-tab changes fire 'storage'; same-tab changes (login,
        // impersonation, org-switch) fire our 'aa:identity-changed' event —
        // replacing the old 100ms localStorage poll that caused the shell's
        // refetch storm.
        window.addEventListener('storage', handleStorageChange);
        window.addEventListener(IDENTITY_CHANGED_EVENT, handleStorageChange);

        // Initial check
        handleStorageChange();

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener(IDENTITY_CHANGED_EVENT, handleStorageChange);
        };
    }, [clientId, userRole]);

    // Listen for open-chat custom event from any page
    useEffect(() => {
        const handleOpenChat = () => setShowChat(true);
        window.addEventListener('open-chat', handleOpenChat);
        return () => window.removeEventListener('open-chat', handleOpenChat);
    }, []);

    // Track how many toasts are live so we can offer a single "Clear all" control
    // when the stack has more than one (bottom-left, above the stack).
    const [toastCount, setToastCount] = useState(0);
    useEffect(() => {
        const unsub = toast.onChange((payload: any) => {
            setToastCount((c) => {
                if (payload.status === 'added') return c + 1;
                if (payload.status === 'removed') return Math.max(0, c - 1);
                return c;
            });
        });
        return () => { if (typeof unsub === 'function') unsub(); };
    }, []);

    // Layout elements as plain JSX — NOT components defined inside App's
    // render. A component defined here is a new function type on every App
    // re-render, and React Router then unmounts and remounts the ENTIRE page
    // tree under it: every mounted page refired its mount effects (data
    // fetches) on each App render, which is exactly the /my-business request
    // flood. Inline JSX keeps the element types (ProtectedRoute/Layout)
    // stable, so re-renders reconcile in place instead of remounting.
    const layoutWithAuth = (
        <ProtectedRoute>
            <Layout onShowChat={() => setShowChat(true)} clientId={clientId}>
                <Outlet />
            </Layout>
        </ProtectedRoute>
    );

    // Page not found sits in the shell this person normally works in: the staff
    // shell for a partner or super, the company shell for everyone else.
    let notFoundShell: React.ReactNode = (
        <Layout onShowChat={() => setShowChat(true)} clientId={clientId} keepPath>
            <Outlet />
        </Layout>
    );
    if (userRole === 'consultant' || userRole === 'super') notFoundShell = <StaffShell />;
    const notFoundWithAuth = <ProtectedRoute>{notFoundShell}</ProtectedRoute>;

    // Staff shell: the Solution Partner portal for a consultant, the super
    // console for platform staff. StaffShell makes that one choice.
    const consultantLayoutWithAuth = (
        <ProtectedRoute>
            <StaffShell />
        </ProtectedRoute>
    );

    // Debug logging
    console.log('App render:', { clientId, userRole });

    return (
        <ImpersonationProvider>
            <div className="min-h-screen">
                {/* Bottom-left, STACKED: multiple toasts collapse into one neat
                    pile that expands on hover (like the builder's sonner), instead
                    of the old top-right column. Click a card to dismiss it; they
                    auto-close, and only a few show at once. Lifted a little to
                    leave room for the "Clear all" control below. */}
                <ToastContainer
                    position="bottom-right"
                    stacked
                    limit={6}
                    newestOnTop
                    autoClose={4000}
                    closeOnClick
                    pauseOnHover
                    theme="light"
                    icon={false}
                    className="aa-toast-container"
                    style={{ zIndex: 99999, bottom: '3.25rem' }}
                />
                {/* One-click clear-all — bottom-right, only when >1 toast is up. */}
                {toastCount > 1 && (
                    <button
                        onClick={() => toast.dismiss()}
                        style={{ position: 'fixed', right: '1rem', bottom: '1rem', zIndex: 100000 }}
                        className="px-3 py-1.5 text-[11px] font-semibold text-white bg-[#0d1b34]/90 hover:bg-[#0d1b34] rounded-full shadow-lg backdrop-blur transition-colors"
                    >
                        Clear all ({toastCount})
                    </button>
                )}
                
                <Routes>
                    {/* Public Routes - No Authentication Required */}
                    
                    {/* Auth pages */}
                    <Route element={<MarketingLayout />}>
                        <Route path="/login" element={<Login />} />
                        <Route path="/signup" element={<Signup />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/rates" element={<RateCard />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="/invite/:token" element={<InviteAccept />} />
                        {/* A Solution Partner's accept link: choose a password, land on the welcome deck. */}
                        <Route path="/partner/join/:token" element={<PartnerJoin />} />
                    </Route>
                    <Route path="/oauth/callback" element={<OAuthCallback />} />

                    {/* Claude connector consent — the builder sends consultants
                        here to sign in and approve; the page redirects them
                        straight back to Claude. Own shell, no dashboard. */}
                    <Route path="/connect-claude" element={<ConnectClaude />} />

                    {/* /onboarding route removed — Orbit Intake at /orbit/intake
                        is the canonical post-signup landing. Returning users
                        route via resolvePostLoginRoute → route_plan. */}

                    {/* Standalone preview page - public for consultant sharing */}
                    <Route
                        path="/preview/:clientId"
                        element={<StandalonePreview />}
                    />

                    {/* Secure tokenized form (public, no auth) — for PHI workflow steps */}
                    <Route path="/f/:token" element={<SecureFormPage />} />

                    {/* Direct entry — the public front door for a company
                        arriving without a Solution Partner. Full-bleed, outside
                        MarketingLayout: it carries its own topbar. Every route
                        out of it is an existing one (signup → Orbit intake,
                        /invite/:token, /login). Not /onboarding — that path is
                        the authenticated employee onboarding page. */}
                    <Route path="/start" element={<DirectEntry />} />
                    {/* The person-first intake. Public and account-free by
                        design: it is scanned off a slide by somebody who has no
                        login and is answering about their own work, not their
                        company. The ?ref= names the Solution Partner. */}
                    <Route path="/me" element={<PersonIntake />} />
                    {/* The direct-entry company setup. Public: it is what
                        creates the account, so it cannot sit behind one. */}
                    <Route path="/start/company" element={<LegacyCompanySetupRedirect />} />
                    {/* Where direct-entry setup lands: the draft it prepared,
                        stated as a draft. Authenticated by the account the
                        wizard just created. */}
                    <Route path="/start/ready" element={<WorkspaceReady />} />

                    {/* Public SDK docs */}
                    <Route path="/docs/sdk" element={<SdkDocsPage />} />

                    {/* Orbit Layer 1 intake — full-screen, no Layout shell.
                        Phase 3 ships UI-only; guest-friendly through §D,
                        sign-up gate at D→E goes in Phase 5. */}
                    <Route path="/orbit/intake" element={<OrbitIntake />} />
                    {/* Ask Orbit — the employee's request intake. Full-screen and
                        outside the Layout shell: the answers here set the route,
                        the owner and the controls, which is not a side-drawer
                        decision. Authenticated, unlike /orbit/intake above it:
                        this writes a record that belongs to a named person in a
                        named company. */}
                    <Route
                        path="/orbit/request"
                        element={<ProtectedRoute><OrbitRequest /></ProtectedRoute>}
                    />
                    {/* The deck: what Orbit read and wants confirmed, then the
                        cards waiting on this person. Full-screen with its own
                        topbar, like the intake: this is where an invite link and
                        a sign-up land, before the person has met the portal. */}
                    <Route
                        path="/orbit/deck"
                        element={<ProtectedRoute><DeckPage /></ProtectedRoute>}
                    />
                    {/* The setup every company member walks once, whatever the role:
                        welcome, about you, apps, permissions, ideas, connect, start. */}
                    <Route
                        path="/setup"
                        element={<ProtectedRoute><SetupPage /></ProtectedRoute>}
                    />
                    {/* The partner's own deck, seen once after the invite link:
                        framework choice, the cards their clients get, their link. */}
                    <Route
                        path="/partner/welcome"
                        element={<ProtectedRoute><PartnerWelcome /></ProtectedRoute>}
                    />
                    {/* /orbit/options/:engagementId AND /experience-report/:engagementId
                        are mounted INSIDE LayoutWithAuth below so the dashboard
                        sidebar wraps them — they're milestone surfaces, not
                        marketing-style standalone pages. */}
                    {/* /orbit/scope/:engagementId is mounted INSIDE LayoutWithAuth
                        below so the dashboard sidebar wraps it. Kept as its own
                        URL (not a MyBusiness tab) per the v2.7 lane structure —
                        Scope is a deep work surface, not a peer tab to Builds. */}
                    {/* Orbit Stage 2 outcome surfaces — bypass-eligible speed-rails.
                        Each picks up from /api/intake/engagement/:eid and fires the
                        right action (deploy / template build / planner handoff). */}
                    <Route path="/orbit/deploy/:engagementId" element={<OrbitDeploy />} />
                    <Route path="/orbit/template/:engagementId" element={<OrbitTemplate />} />
                    <Route path="/orbit/handoff/:engagementId" element={<OrbitHandoff />} />

                    {/* Delegated share — PUBLIC, no auth, no Layout shell.
                        A teammate opens the magic link and fills the cards
                        the owner shared. The token in the URL is the auth
                        boundary (validated server-side). */}
                    <Route path="/orbit/delegated/:token" element={<OrbitDelegated />} />

                    {/* Pre-authenticated intake link — PUBLIC, no auth shell.
                        Consultant shares /orbit/enter/<token>; we exchange the
                        token for a client session and redirect to /orbit/intake. */}
                    <Route path="/orbit/enter/:token" element={<OrbitEnter />} />

                    {/* Root redirect - check if authenticated */}
                    <Route path="/" element={(() => {
                        if (!localStorage.getItem('auth_token')) return <Navigate to="/login" replace />;
                        if (userRole === 'consultant') return <Navigate to="/partner/framework" replace />;
                        if (userRole === 'super') return <Navigate to="/super/business/overview" replace />;
                        return <ClientLanding />;
                    })()} />
                    
                    {/* Consultant/Super Routes - Dark sidebar layout */}
                    <Route element={consultantLayoutWithAuth}>
                        <Route element={<RoleBasedRoute allowedRoles={['consultant', 'super']} />}>
                            <Route path="consultant" element={<Navigate to="/partner/framework" replace />} />
                            {/* The old consultant dashboard is retired: a Solution
                                Partner's home is the client console under /partner. */}
                            <Route path="consultant/home" element={<Navigate to="/partner/framework" replace />} />
                            <Route path="consultant/dashboard" element={<ConsultantDashboard />} />
                            {/* AI Adoption is hidden for now; its old links land on the role home.
                            <Route path="consultant/aai" element={<AaiWorkspace />} />
                            Consultant AAI delivery cockpit (design §9). Declared
                                before :companyId so the static segments win.
                            <Route path="consultant/aai/programs" element={<ConsultantProgramsPage />} />
                            <Route path="consultant/aai/delivery" element={<ConsultantDeliveryPage />} />
                            <Route path="consultant/aai/assets" element={<ConsultantAssetsPage />} />
                            Drill-down: one client's AI-adoption status (read-only, ?company_id-scoped).
                            <Route path="consultant/aai/:companyId" element={<ConsultantClientAdoption />} />
                            */}
                            <Route path="consultant/aai/*" element={<Navigate to="/" replace />} />
                            {/* Old agentic onboarding chat retired — replaced by the
                                create-client form. Redirect any stale links. */}
                            <Route path="consultant/onboarding" element={<Navigate to="/consultant/clients/new" replace />} />
                            {/* Declared BEFORE clients/:clientId so "new" isn't captured as an id. */}
                            <Route path="consultant/clients/new" element={<NewClient />} />
                            <Route path="consultant/qa-validation" element={<Navigate to="/knowledge#conversations" replace />} />
                            <Route path="consultant/learned-qa/:clientId" element={<Navigate to="/knowledge#conversations" replace />} />
                            <Route path="consultant/fine-tuning/:clientId" element={<Navigate to="/knowledge#training" replace />} />
                            <Route path="consultant/seo-center" element={<SEOCenter />} />
                            <Route path="consultant/support-flows" element={<SupportFlowEditor />} />
                            <Route path="consultant/clients/:clientId" element={<ClientDetail />} />
                            <Route path="consultant/agents" element={<AgentsManagement />} />
                            <Route path="consultant/settings" element={<ConsultantSettings />} />
                            <Route path="consultant/seo-dashboard" element={<Navigate to="/consultant/seo-center" replace />} />
                            <Route path="consultant/seo-analysis" element={<Navigate to="/consultant/seo-center" replace />} />
                            <Route path="consultant/crawl-progress/:clientId" element={<CrawlDetails />} />
                        </Route>

                        {/* The Solution Partner console. Consultant only: a super
                            spans every account and has no single assigned client,
                            which is the whole premise of these four screens. */}
                        <Route element={<RoleBasedRoute allowedRoles={['consultant']} />}>
                            <Route path="partner" element={<Navigate to="/partner/framework" replace />} />
                            {/* The Dashboard is parked for now; the Intake framework is
                                the partner's first tab. */}
                            {/* <Route path="partner/dashboard" element={<PartnerDashboardPage />} /> */}
                            <Route path="partner/clients" element={<PartnerClientsPage />} />
                            <Route path="partner/client" element={<PartnerClientOverviewPage />} />
                            <Route path="partner/client/settings" element={<PartnerClientSettings />} />
                            {/* <Route path="partner/schedule" element={<PartnerSchedulePage />} /> */}
                            <Route path="partner/projects" element={<PartnerProjectsPage />} />
                            <Route path="partner/context" element={<PartnerClientContextPage />} />
                            {/* The engagement as a project space: the journey's
                                phases, with each Orbit conversation as a ticket. */}
                            {/* <Route path="partner/engagement" element={<PartnerEngagementBoard />} /> */}
                            {/* Orbit Control draws over the shell, but it is a
                                route so a half-finished collection survives a
                                refresh and can be linked to. */}
                            <Route path="partner/projects/:requestId" element={<PartnerDeliveryProject />} />
                            <Route path="partner/clients/new" element={<PartnerAddClient />} />
                            {/* Your framework: the questions that open Orbit's
                                conversation, and the dossier they fill, for the
                                clients this partner adds. The first nav item, and
                                also reached from Clients, client settings and the Room. */}
                            <Route path="partner/framework" element={<FrameworkPage />} />
                            <Route path="partner/settings" element={<PartnerSettings />} />
                            {/* Notifications sent before a screen was named, or
                                after it moved, still carry the old address, so
                                those keep landing. Any other address says it is
                                not found instead of quietly opening another page. */}
                            <Route path="partner/dashboard" element={<Navigate to="/partner/framework" replace />} />
                            <Route path="partner/orbit/*" element={<Navigate to="/partner/clients" replace />} />
                            <Route path="partner/*" element={<PartnerNotFound />} />
                        </Route>

                        {/* Super-only Routes — own /super/* namespace (super is a
                            different role from consultant; these surfaces are the
                            platform's, not a consultant's). */}
                        <Route element={<RoleBasedRoute allowedRoles={['super']} />}>
                            {/* The deck IS the super app: Business/Machine live in
                                the shell header, the sidebar swaps per mode, and
                                each page renders from /super/:mode/:page. */}
                            <Route path="super/command-deck" element={<Navigate to="/super/business/overview" replace />} />
                            <Route path="super/business/aa" element={<Navigate to="/super/business/overview" replace />} />
                            <Route path="super/business/usage" element={<Navigate to="/super/machine/usage" replace />} />
                            <Route path="super/business/ai-cost" element={<Navigate to="/super/machine/ai-cost" replace />} />
                            <Route path="super/machine/margin" element={<Navigate to="/super/machine/ai-cost" replace />} />
                            <Route path="super/machine/cost" element={<Navigate to="/super/machine/routing" replace />} />
                            <Route path="super/machine/usage/:companyId" element={<CommandDeck />} />
                            {/* AWS hosting is hidden for now; its links land on Machine Overview.
                            <Route path="super/machine/hosting/:billId" element={<CommandDeck />} />
                            */}
                            <Route path="super/machine/hosting/*" element={<Navigate to="/super/machine/overview" replace />} />
                            <Route path="super/machine/hosting" element={<Navigate to="/super/machine/overview" replace />} />
                            <Route path="super/business/:deckTab" element={<CommandDeck />} />
                            <Route path="super/machine/projects/:projectId" element={<CommandDeck />} />
                            <Route path="super/machine/:deckTab" element={<CommandDeck />} />
                            {/* The per-role intake tabs are replaced by the fixed sections every client answers.
                            <Route path="super/field-studio" element={<FieldStudio />} />
                            */}
                            <Route path="super/roles-and-intake" element={<RolesAndIntake />} />
                            <Route path="super/field-studio" element={<Navigate to="/super/roles-and-intake?tab=intake" replace />} />
                            {/* <Route path="super/mcp-catalog" element={<McpCatalog />} /> */}
                            <Route path="super/role-management" element={<Navigate to="/super/roles-and-intake?tab=roles" replace />} />
                            <Route path="super/team-overview" element={<TeamOverview />} />
                            <Route path="super/consultants" element={<ConsultantsList />} />
                            <Route path="super/plan-management" element={<PlansAndPayments />} />
                            <Route path="super/consultants/new" element={<ConsultantForm />} />
                            <Route path="super/consultants/:id" element={<ConsultantDetails />} />
                        </Route>
                    </Route>

                    {/* /expert is retired (provider-tenant model): an expert is a
                        normal client-shell member who reaches the builder via the
                        "Builder" sidebar link. Redirect any stale link home. */}
                    <Route path="expert" element={<Navigate to="/dashboard" replace />} />

                    {/* Client Routes - Blue sidebar layout */}
                    <Route element={layoutWithAuth}>
                        {/* Shared Routes - Accessible by consultants, super, and clients */}
                        <Route element={<RoleBasedRoute allowedRoles={['consultant', 'super', 'client', 'client_admin']} />}>
                            <Route path="crawl-details/:clientId" element={<CrawlDetails />} />
                        </Route>

                        {/* Client Routes */}
                        <Route element={<RoleBasedRoute allowedRoles={['client', 'client_admin']} />}>
                            {/* Primary routes */}
                            {/* Phase 5b: platform-shaped dashboard. Legacy
                                UnifiedAnalytics is kept for consultant /
                                impersonation views via /consultant/* — clients
                                land on the new ClientDashboard surface. */}
                            <Route path="dashboard" element={<ClientDashboard />} />
                            {/* The Problems LIST is what Projects replaced: the
                                same rows, beside the work they turn into,
                                under the word the rest of the product uses.
                                Stage 1 retired every link into it; the page
                                itself stayed reachable by address alone, which
                                is a second list of the same things nobody was
                                sent to. The per-problem page is a different
                                matter and stays: notifications, and a scoping
                                row on Projects, both open one by id. */}
                            <Route path="problems" element={<Navigate to="/projects?tab=opportunities" replace />} />
                            <Route path="problems/:problemId" element={<ProblemDetail />} />
                            <Route path="billing/invoices" element={<OwnerInvoicesPage />} />
                            <Route path="billing/usage/:key" element={<OwnerPersonUsagePage />} />
                            <Route path="billing/project-budgets/:projectId" element={<OwnerProjectBudgetPage />} />
                            <Route path="projects/opportunities/:id" element={<OpportunityPage />} />
                            <Route path="projects/quick-wins/:id" element={<QuickWinPage />} />
                            <Route path="projects/:requestId" element={<ProjectRecordPage />} />
                            <Route path="projects" element={<PortalSwitch owner={<OwnerProjectsPage />} department={<DepartmentProjectsPage />} champion={<ChampionProjectsPage />} other={<ProjectsScreen />} />} />
                            <Route element={<PermissionRoute permission="aai_view_company_dashboard" />}>
                                {/* Portfolio is hidden: Projects > Opportunities covers it. Old links land there.
                                <Route path="portfolio" element={<PortalSwitch owner={<OwnerPortfolioPage />} champion={<ChampionPortfolioPage />} other={<OwnerPortfolioPage />} />} />
                                */}
                                <Route path="portfolio" element={<Navigate to="/projects?tab=opportunities" replace />} />
                                <Route path="registry" element={<ChampionRegistryPage />} />
                                <Route path="vendors" element={<ChampionVendorsPage />} />
                                {/* Controls is a tab on Company Context, and only there.
                                    Every link ever sent to this address still lands on it. */}
                                <Route path="controls" element={<Navigate to="/company-context?tab=controls" replace />} />
                                <Route path="controls/ai" element={<OwnerAiCapabilitiesPage />} />
                                {/* The Orbit Room is the top of Company Context for every
                                    role, so these are all one address. */}
                                <Route path="room" element={<Navigate to="/company-context" replace />} />
                                <Route path="client" element={<Navigate to="/company-context" replace />} />
                                <Route path="adoption" element={<Navigate to="/company-context" replace />} />
                            </Route>
                            {/* Which questions Orbit asks is the platform's and
                                the Solution Partner's decision, not a client's.
                                The old owner-facing editor now lands on the
                                company page it used to be reached from. */}
                            <Route path="framework" element={<Navigate to="/company-context" replace />} />
                            <Route path="agents" element={<Navigate to="/projects" replace />} />
                            {/* Standalone /whatsapp pages were removed — WhatsApp lives
                                inside the marketplace chatbot detail. Redirect any old
                                bookmarks so they land in the right place. */}
                            <Route path="whatsapp" element={<Navigate to="/marketplace/chatbot?channel=whatsapp#channels" replace />} />
                            <Route path="whatsapp/workflows" element={<Navigate to="/marketplace/chatbot?channel=whatsapp#channels" replace />} />
                            {/* B5 — a real page. This used to redirect to
                                Settings, so every "manage your team" link
                                landed somewhere the user had not asked for. */}
                            {/* People and departments are managed in Settings,
                                under People & departments, the section named
                                after them. /team mounted the same component at
                                a second address, so "manage your team" had two
                                answers and only one of them was in Settings.
                                The address opens that section, because invite
                                emails and notifications have linked here for as
                                long as it has existed. The Owner is the exception:
                                their sidebar has a People & departments row, so
                                for them the address is a screen rendering the
                                same panel Settings does. */}
                            <Route path="team" element={<PortalSwitch owner={<PeoplePage />} champion={<PeoplePage />} other={<OpenPeopleSettings />} />} />
                            <Route path="usage" element={<PortalSwitch owner={<UsagePage />} other={<Navigate to="/dashboard" replace />} />} />
                            <Route path="connectors" element={<PortalSwitch owner={<ConnectorsPage />} other={<Navigate to="/dashboard" replace />} />} />
                            <Route path="usage/:key" element={<OwnerPersonUsagePage />} />
                            {/* A provider company's Owner: the clients it serves. */}
                            <Route path="clients" element={<ProviderClientsPage />} />
                            <Route path="clients/projects" element={<ProviderClientProjectsPage />} />
                            <Route path="clients/:clientId" element={<ProviderClientPage />} />
                            <Route path="clients/:clientId/budgets/:projectId" element={<ProviderProjectBudgetPage />} />
                            <Route path="aai" element={<PortalSwitch owner={<OwnerAdoptionPage />} department={<DepartmentAdoptionPage />} champion={<ChampionAdoptionPage />} trainer={<TrainerAdoptionPage />} other={<AdoptionHomeHub />} />} />
                            {/* Participant self-service surfaces (also reachable as
                                tabs inside /aai for other roles). The sidebar
                                promotes these to top-level for participants. */}
                            <Route path="my-readiness" element={<MyReadinessRoute />} />
                            <Route path="my-training" element={<MyTrainingPage />} />
                            <Route path="onboarding" element={<OnboardingPage />} />
                            <Route path="my-department" element={<DepartmentHub />} />
                            <Route path="roster" element={<Navigate to="/my-department?tab=roster" replace />} />
                            {/* Department-head surfaces split out per the imported
                                design: Team Rollup (aggregates) + Use Cases. */}
                            <Route path="team-rollup" element={<Navigate to="/my-department?tab=rollup" replace />} />
                            <Route path="dept-use-cases" element={<DeptUseCasesPage />} />
                            {/* Champion · Spotlights (recognition, never ranking) */}
                            <Route path="spotlights" element={<Navigate to="/aai?tab=spotlights" replace />} />
                            {/* Requests / approval queue + use-case registry */}
                            <Route element={<PermissionRoute permission="aai_request_something" />}>
                                <Route path="requests" element={<RequestsPage />} />
                                <Route path="scope" element={<StartScopingPage />} />
                            </Route>
                            <Route path="notifications" element={<NotificationsPage />} />
                            {/* Every request notification linked here for as long
                                as they have existed, and no route ever declared
                                it. Notifications already sent still carry it, and
                                so do the emails, so this redirect is permanent. */}
                            <Route path="aai/requests" element={<Navigate to="/requests" replace />} />
                            <Route element={<PermissionRoute permission="aai_view_registry" />}>
                                <Route path="aai/registry" element={<PortalSwitch owner={<Navigate to="/registry" replace />} champion={<Navigate to="/registry" replace />} other={<UseCasesHub />} />} />
                            </Route>
                            {/* AAI insight sub-pages (now backed by real endpoints) */}
                            {/* Employee portal. Each of these is a renamed home
                                for surfaces that already existed, so every old
                                address below still resolves on its own. */}
                            <Route path="quick-wins" element={<Navigate to="/my-projects?tab=quick_wins" replace />} />
                            <Route path="my-projects" element={<EmployeeProjectsPage />} />
                            <Route path="ai-adoption" element={<EmployeeAdoptionPage />} />
                            {/* Every role reads results on Home, beside the work that
                                produced them. This address is the one every old
                                link and bookmark still uses. */}
                            <Route path="results" element={<Navigate to="/dashboard#results" replace />} />
                            {/* Documents hidden for now.
                            <Route path="documents" element={<DocumentsPage />} />
                            */}
                            <Route path="company-context" element={<PortalSwitch owner={<OwnerCompanyContextPage />} department={<DepartmentCompanyContextPage />} champion={<ChampionCompanyContextPage />} other={<CompanyContextPage />} />} />
                            <Route path="my-productivity" element={<MyProductivityPage />} />
                            <Route path="my-tools" element={<MyToolsRoute />} />
                            <Route path="aai/my-tools" element={<AaiMyToolsPage />} />
                            <Route path="tools" element={<ToolLibraryPage />} />
                            <Route element={<PermissionRoute permission="aai_build_skills" />}>
                                <Route path="skill-builder" element={<SkillBuilderPage />} />
                                <Route path="skill-builder/workflow" element={<SkillBuilderPage />} />
                            </Route>
                            <Route element={<PermissionRoute permission="aai_view_org_chart" />}>
                                <Route path="org-chart" element={<PeopleHub />} />
                            </Route>
                            <Route path="whats-allowed" element={<Navigate to="/help-community?tab=allowed" replace />} />
                            <Route path="help-community" element={<HelpHub />} />
                            <Route element={<PermissionRoute permission="aai_view_governance" />}>
                                <Route path="risk-governance" element={<Navigate to="/aai?tab=governance" replace />} />
                            </Route>
                            <Route path="reports" element={<ProgramReportsPage />} />
                            <Route element={<PermissionRoute permission="aai_view_impact" />}>
                                <Route path="aai/impact" element={<Navigate to="/aai?tab=impact" replace />} />
                            </Route>
                            <Route path="aai-program" element={<AaiProgramPage />} />
                            {/* Trainer cockpit (Phase 4/5) + participant sessions */}
                            <Route path="aai/curriculum" element={<ProgramHub />} />
                            <Route path="aai/templates" element={<Navigate to="/aai/curriculum?tab=templates" replace />} />
                            <Route path="aai/delivery" element={<DeliveryPage />} />
                            <Route path="aai/cohorts" element={<CohortsPage />} />
                            {/* The Trainer's roster screen. It is inside the People hub for the
                                roles that have one; on its own it still asks for the same
                                grant, so it is not an open address. */}
                            <Route element={<PermissionRoute permission="aai_view_org_chart" />}>
                              <Route path="aai/departments" element={<DepartmentsAdminPage />} />
                            </Route>
                            <Route path="aai/scoring" element={<Navigate to="/aai?tab=scoring" replace />} />
                            <Route element={<PermissionRoute permission="aai_view_marketplace" />}>
                                <Route path="aai/skills" element={<SkillsMarketplacePage />} />
                            </Route>
                            <Route element={<PermissionRoute permission="aai_evaluate_vendors" />}>
                                <Route path="aai/vendors" element={<PortalSwitch owner={<VendorsRedirect />} champion={<VendorsRedirect />} other={<Navigate to="/aai/registry?tab=vendors" replace />} />} />
                            </Route>
                            <Route element={<PermissionRoute permission="aai_review_prd" />}>
                                <Route path="aai/prd-reviews" element={<PrdReviewsPage />} />
                                {/* The expert's queue: requests named to them. The
                                    Requests page answers "what would help?"; this one
                                    answers "what am I on". */}
                                <Route path="my-work" element={<MyWorkPage />} />
                            </Route>
                            <Route path="my-sessions" element={<MySessionsPage />} />
                            <Route path="capabilities/new" element={<CapabilityStudioPage />} />
                            {/* The knowledge store browser lists the company's own
                                sources and connected apps. Seeing that a source
                                exists is a connection-management read, so it asks
                                for that grant; the plain client shell passes as
                                before. */}
                            <Route element={<PermissionRoute permission="aai_manage_connectors" />}>
                                <Route path="knowledge" element={<KnowledgeBrowser />} />
                            </Route>
                            <Route path="marketplace" element={<MarketplacePage />} />
                            <Route path="marketplace/chatbot/preparing" element={<ChatbotPreparing />} />
                            <Route path="marketplace/chatbot" element={<ChatbotDetailPage />} />
                            {/* Every other /marketplace/<slug> (Orbit links, catalog
                                landing_urls, bookmarks) resolves to the page that
                                serves it, or back to the Marketplace, never blank. */}
                            <Route path="marketplace/:slug" element={<MarketplaceSolutionRedirect />} />
                            <Route path="my-business" element={<MyBusinessHome />} />
                            {/* Retired: the standalone /orbit/scope L2 chat page.
                                Scoping now lives on the per-problem page and in
                                the Workspace; company data lives on
                                /my-business. Any stale link lands on Projects. */}
                            <Route path="orbit/scope/:engagementId" element={<Navigate to="/projects?tab=opportunities" replace />} />
                            {/* Stage 1 Solution Options — the 3-card screen
                                (Marketplace / Template / Custom). Sidebar-wrapped
                                so the customer doesn't lose the dashboard chrome
                                when they leave /my-business to pick a path. */}
                            <Route path="orbit/options/:engagementId" element={<OrbitOptions />} />
                            {/* Experience Report — v2.7 auto-generated milestone
                                artifact. Sidebar-wrapped. Deep-linkable from
                                /my-business and /orbit/scope. */}
                            <Route path="experience-report/:engagementId" element={<ExperienceReportPage />} />
                            {/* Settings is the modal the profile menu opens.
                                This page was a second one, with its own tab row
                                over mostly the same panels, so a tab added to
                                one of them was a tab most people never saw. The
                                address stays and opens the modal on the nearest
                                section, because plenty of links carry ?tab=. */}
                            <Route path="settings" element={<OpenSettings />} />
                            {/* Redirects: old routes → new marketplace/chatbot pages */}
                            <Route path="theme-editor" element={<Navigate to="/marketplace/chatbot#customize" replace />} />
                            <Route path="knowledge-base" element={<Navigate to="/marketplace/chatbot#customize" replace />} />
                            <Route path="chatbot-training" element={<Navigate to="/marketplace/chatbot#customize" replace />} />
                            <Route path="re-upload" element={<Navigate to="/marketplace/chatbot#customize" replace />} />
                            <Route path="upload" element={<Navigate to="/marketplace/chatbot#customize" replace />} />
                            <Route path="chat-history" element={<Navigate to="/marketplace/chatbot#stats" replace />} />
                            <Route path="prompts" element={<Navigate to="/marketplace/chatbot#customize" replace />} />
                            <Route path="learned-qa" element={<Navigate to="/marketplace/chatbot#customize" replace />} />
                            <Route path="fine-tuning" element={<Navigate to="/marketplace/chatbot#customize" replace />} />
                            <Route path="citation-analytics" element={<Navigate to="/marketplace/chatbot#customize" replace />} />
                            <Route path="citations" element={<Navigate to="/marketplace/chatbot#customize" replace />} />
                            <Route path="token-usage-analytics" element={<Navigate to="/dashboard" replace />} />
                            <Route path="integrations" element={<Navigate to="/marketplace/chatbot#channels" replace />} />
                            <Route path="calendar-settings" element={<Navigate to="/settings?tab=calendar" replace />} />
                            <Route path="profile" element={<Navigate to="/settings?tab=general" replace />} />
                            <Route path="client/billing" element={<ClientBillingDashboard />} />
                            <Route path="client/payments" element={<Navigate to="/client/billing?tab=payments" replace />} />
                            {/* /billing alias — the workspace pending payload, older
                                emails, and historical links all point at /billing.
                                Redirect rather than 404. */}
                            <Route path="billing" element={<PortalSwitch owner={<BillingPage />} other={<Navigate to="/client/billing" replace />} />} />
                        </Route>
                    </Route>

                    {/* Any other address: Page not found inside the signed-in
                        person's own shell, not a blank page or a silent bounce. */}
                    <Route element={notFoundWithAuth}>
                        <Route path="*" element={<NotFound />} />
                    </Route>
                </Routes>
                
                {showChat && (
                    <ChatInterface 
                        clientId={clientId} 
                        onClose={() => setShowChat(false)} 
                    />
                )}
                
                {/* Global crawl progress indicator */}
                <CrawlProgressIndicator />
            </div>
        </ImpersonationProvider>
    );
}


export default App;
